/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * AgentKpiStrip
 * A compact KPI row used in the redesigned Agent Detail page to surface the
 * most important counts at a glance without consuming the vertical space of
 * the old card grid.
 */

import React from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import type { SvgIconTypeMap } from '@mui/material/SvgIcon';
import {
  Build as ToolIcon,
  History as HistoryIcon,
  Memory as MemoryIcon,
  TrendingUp as TrendIcon,
  Schedule as ClockIcon,
} from '@mui/icons-material';
import { formatStoredDate, formatStoredDateTime } from '../../../utils/datetime';

type IconType = OverridableComponent<SvgIconTypeMap<unknown, 'svg'>> & { muiName: string };

interface Kpi {
  label: string;
  value: React.ReactNode;
  secondary?: string;
  icon: IconType;
  tooltip?: string;
}

interface AgentKpiStripProps {
  toolCount: number;
  enabledToolCount: number;
  executionCount: number;
  successRate?: number | null;
  memoryCount: number;
  avgImportance?: number | null;
  lastRunAt?: string | null;
  loading?: boolean;
}

const formatRelative = (iso?: string | null): string => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  const diff = Date.now() - d.getTime();
  if (diff < 0) return formatStoredDateTime(d, undefined, 'Never');
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatStoredDate(d, undefined, 'Never');
};

export const AgentKpiStrip: React.FC<AgentKpiStripProps> = ({
  toolCount,
  enabledToolCount,
  executionCount,
  successRate,
  memoryCount,
  avgImportance,
  lastRunAt,
  loading = false,
}) => {
  const theme = useTheme();

  const kpis: Kpi[] = [
    {
      label: 'Tools',
      value: toolCount,
      secondary: `${enabledToolCount} enabled`,
      icon: ToolIcon as IconType,
      tooltip: 'Tools assigned to this agent',
    },
    {
      label: 'Executions',
      value: executionCount,
      secondary:
        typeof successRate === 'number'
          ? `${Math.round(successRate * 100)}% success`
          : 'No data',
      icon: HistoryIcon as IconType,
      tooltip: 'Total recorded executions',
    },
    {
      label: 'Memories',
      value: memoryCount,
      secondary:
        typeof avgImportance === 'number'
          ? `Avg importance ${avgImportance.toFixed(2)}`
          : undefined,
      icon: MemoryIcon as IconType,
      tooltip: 'Long- and short-term memories stored',
    },
    {
      label: 'Last run',
      value: formatRelative(lastRunAt),
      secondary: lastRunAt ? formatStoredDateTime(lastRunAt, undefined, 'No executions yet') : 'No executions yet',
      icon: ClockIcon as IconType,
      tooltip: 'Most recent execution',
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1, sm: 1.5 },
        mb: { xs: 2, sm: 3 },
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(4, 1fr)',
        },
        gap: { xs: 1, sm: 1.5 },
        overflow: 'hidden',
      }}
    >
      {kpis.map((kpi) => {
        const Icon = kpi.icon;
        const content = (
          <Box
            key={kpi.label}
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: 1,
              borderColor: alpha(theme.palette.primary.main, 0.08),
              transition: 'background-color 120ms ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
              minWidth: 0,
            }}
          >
            <SvgIcon
              component={Icon as unknown as React.ElementType}
              sx={{
                color: 'primary.main',
                fontSize: { xs: 22, sm: 26 },
                flexShrink: 0,
              }}
            />
            <Stack spacing={0.1} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '0.65rem' }}
              >
                {kpi.label}
              </Typography>
              {loading ? (
                <Skeleton width={60} height={22} />
              ) : (
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ lineHeight: 1.15, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}
                  noWrap
                >
                  {kpi.value}
                </Typography>
              )}
              {kpi.secondary ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: '0.7rem' }}
                >
                  {kpi.secondary}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        );
        return kpi.tooltip ? (
          <Tooltip key={kpi.label} title={kpi.tooltip} arrow>
            {content}
          </Tooltip>
        ) : (
          content
        );
      })}
      <Box sx={{ display: 'none' }}>
        <TrendIcon />
      </Box>
    </Paper>
  );
};

export default AgentKpiStrip;
